import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Copy, FileStack, ChevronUp, ChevronDown, Trash2, Check, Clock, Layers } from "lucide-react";

const roleOpts = [{ v: "projektleder", l: "Projektleder" }, { v: "medarbejder", l: "Medarbejder" }, { v: "admin", l: "Administrator" }];
const emptyTpl = { name: "", project_type: "", description: "", default_duration_days: "", prompt_text: "", active: true };

export default function AdminTemplates() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(emptyTpl);
  const [stepsFor, setStepsFor] = useState(null); // template object for step editor
  const [delTarget, setDelTarget] = useState(null); // template pending deletion

  const load = () => api.get("/templates").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const confirmDelete = async () => {
    if (!delTarget) return;
    try {
      await api.delete(`/templates/${delTarget.id}`);
      toast.success("Skabelon slettet");
      setDelTarget(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openNew = () => { setEditing(null); setF(emptyTpl); setOpen(true); };
  const openEdit = (t) => { setEditing(t.id); setF({ name: t.name, project_type: t.project_type || "", description: t.description || "", default_duration_days: t.default_duration_days ?? "", prompt_text: t.prompt_text || "", active: t.active }); setOpen(true); };

  const save = async () => {
    const payload = { ...f, default_duration_days: f.default_duration_days ? parseInt(f.default_duration_days) : null };
    try {
      if (editing) await api.put(`/templates/${editing}`, payload);
      else await api.post("/templates", payload);
      toast.success(editing ? "Skabelon opdateret" : "Skabelon oprettet");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggle = async (t) => { await api.patch(`/templates/${t.id}/toggle`); load(); };
  const copyPrompt = async (text) => {
    try { await navigator.clipboard.writeText(text || ""); toast.success("Prompttekst kopieret"); }
    catch (e) { toast.error("Kunne ikke kopiere automatisk — markér teksten og kopiér manuelt"); }
  };
  const refreshStepsFor = async (tid) => { const { data } = await api.get(`/templates/${tid}`); setStepsFor(data); load(); };

  return (
    <div className="rise-in">
      <div className="flex items-end justify-between mb-8">
        <div><h1 className="text-4xl sm:text-5xl font-light tracking-tight text-[#1A1919]">Projektskabeloner</h1></div>
        <Button onClick={openNew} className="rounded-xl font-medium bg-[#A83232] hover:bg-[#8f2a2a]" data-testid="new-template-btn"><Plus className="h-4 w-4 mr-2" /> Ny skabelon</Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {items.map((t) => (
          <div key={t.id} className={`pcard pcard-hover p-6 ${t.active ? "" : "opacity-60"}`} data-testid={`template-card-${t.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold tracking-tight text-[#1A1919]">{t.name}</h3>
                  {t.project_type && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#F3F1EC] text-stone-600 border border-[#EBE8E3]">{t.project_type}</span>}
                </div>
                <p className="text-sm text-muted-foreground mt-1">{t.description}</p>
              </div>
              <Switch checked={t.active} onCheckedChange={() => toggle(t)} data-testid={`toggle-template-${t.id}`} />
            </div>
            <div className="flex flex-wrap gap-2 mt-4 text-xs text-muted-foreground">
              <span>{t.steps?.length || 0} trin</span>·<span>{t.steps?.filter((s) => s.active).reduce((a, s) => a + (s.default_duration_days || 0), 0)} dage</span>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              <Button size="sm" className="rounded-xl" onClick={() => setStepsFor(t)} data-testid={`edit-steps-${t.id}`}><FileStack className="h-3.5 w-3.5 mr-1.5" /> Administrér trin</Button>
              <Button size="sm" variant="outline" className="rounded-xl" onClick={() => openEdit(t)} data-testid={`edit-template-${t.id}`}><Pencil className="h-3.5 w-3.5 mr-1.5" /> Rediger</Button>
              <Button size="sm" variant="ghost" className="rounded-xl" onClick={() => copyPrompt(t.prompt_text)} data-testid={`copy-prompt-${t.id}`}><Copy className="h-3.5 w-3.5 mr-1.5" /> Kopiér prompt</Button>
              <Button size="sm" variant="ghost" className="rounded-xl text-destructive hover:text-destructive ml-auto" onClick={() => setDelTarget(t)} data-testid={`delete-template-${t.id}`}><Trash2 className="h-3.5 w-3.5 mr-1.5" /> Slet</Button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full p-12 text-center bg-card border border-border rounded-xl"><FileStack className="h-10 w-10 mx-auto text-muted-foreground/40" /><p className="mt-3 text-muted-foreground">Ingen skabeloner endnu.</p></div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl sm:max-w-[560px]">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <span className="h-10 w-10 rounded-xl bg-[#F1DEDA] text-[#A83232] flex items-center justify-center shrink-0"><FileStack className="h-5 w-5" strokeWidth={1.75} /></span>
              <div>
                <DialogTitle className="text-xl font-semibold leading-tight">{editing ? "Rediger skabelon" : "Ny projektskabelon"}</DialogTitle>
                <DialogDescription className="text-sm text-stone-500">Angiv grundoplysninger. Trin administreres separat.</DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="space-y-5 mt-1">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Skabelonnavn</Label>
                <div className="relative">
                  <FileStack className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" strokeWidth={1.75} />
                  <Input data-testid="tpl-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-xl h-11 pl-9 bg-white" placeholder="Fx Stort udviklingsprojekt" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Projekttype</Label>
                <div className="relative">
                  <Layers className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" strokeWidth={1.75} />
                  <Input data-testid="tpl-type" value={f.project_type} onChange={(e) => setF({ ...f, project_type: e.target.value })} className="rounded-xl h-11 pl-9 bg-white" placeholder="Fx Internt" />
                </div>
              </div>
            </div>
            <div className="space-y-1.5"><Label className="text-sm font-medium">Beskrivelse</Label><Textarea data-testid="tpl-desc" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} className="rounded-xl bg-white" /></div>
            <div className="space-y-1.5 max-w-[220px]">
              <Label className="text-sm font-medium">Standardvarighed</Label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" strokeWidth={1.75} />
                <Input data-testid="tpl-duration" type="number" value={f.default_duration_days} onChange={(e) => setF({ ...f, default_duration_days: e.target.value })} className="rounded-xl h-11 pl-9 pr-12 bg-white" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-stone-400 pointer-events-none">dage</span>
              </div>
            </div>
            <div className="space-y-1.5"><Label className="text-sm font-medium">Prompttekst til Lovable-projekt</Label><Textarea data-testid="tpl-prompt" value={f.prompt_text} onChange={(e) => setF({ ...f, prompt_text: e.target.value })} rows={3} className="rounded-xl bg-white" /></div>
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" className="rounded-xl border-[#EBE8E3]" onClick={() => setOpen(false)}>Annullér</Button>
            <Button className="rounded-xl font-medium bg-[#A83232] hover:bg-[#8f2a2a]" onClick={save} data-testid="save-template-btn">Gem skabelon</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {stepsFor && <StepsEditor template={stepsFor} onClose={() => setStepsFor(null)} onChanged={() => refreshStepsFor(stepsFor.id)} />}

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slet skabelon?</AlertDialogTitle>
            <AlertDialogDescription>
              Skabelonen "{delTarget?.name}" og dens trin slettes permanent. Allerede oprettede projekter påvirkes ikke.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-template">Annullér</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={confirmDelete} data-testid="confirm-delete-template">Slet</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StepsEditor({ template, onClose, onChanged }) {
  const steps = template.steps || [];
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ name: "", description: "", default_duration_days: 0, default_responsible_role: "projektleder", default_selected: true });

  const addStep = async () => {
    if (!draft.name.trim()) return toast.error("Trin skal have et navn");
    try {
      await api.post(`/templates/${template.id}/steps`, { ...draft, default_duration_days: parseInt(draft.default_duration_days) || 0 });
      setDraft({ name: "", description: "", default_duration_days: 0, default_responsible_role: "projektleder", default_selected: true });
      setAdding(false); onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const move = async (idx, dir) => {
    const ids = steps.map((s) => s.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    await api.post(`/templates/${template.id}/steps/reorder`, { ordered_ids: ids });
    onChanged();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-2xl max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-xl bg-[#F1DEDA] text-[#A83232] flex items-center justify-center shrink-0"><FileStack className="h-5 w-5" strokeWidth={1.75} /></span>
            <div>
              <DialogTitle className="text-xl font-semibold leading-tight">Trin – {template.name}</DialogTitle>
              <DialogDescription className="text-sm text-stone-500">Tilføj, rediger, omarrangér og aktivér/deaktivér trin. Ændringer gemmes automatisk.</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="space-y-3 mt-1" data-testid="template-steps-list">
          {steps.map((s, i) => <StepRow key={s.id} step={s} index={i} count={steps.length} onMove={move} onChanged={onChanged} />)}
          {steps.length === 0 && <p className="text-sm text-muted-foreground">Ingen trin endnu.</p>}
        </div>

        {adding ? (
          <div className="border border-[#EBE8E3] rounded-2xl p-4 space-y-3 bg-[#FBFAF8]" data-testid="new-step-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5 sm:col-span-2"><Label className="text-sm font-medium">Trinnavn</Label><Input data-testid="new-step-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="rounded-xl h-10 bg-white" /></div>
              <div className="space-y-1.5"><Label className="text-sm font-medium">Varighed (dage)</Label><Input type="number" min="0" data-testid="new-step-duration" value={draft.default_duration_days} onChange={(e) => setDraft({ ...draft, default_duration_days: e.target.value })} className="rounded-xl h-10 bg-white" /></div>
              <div className="space-y-1.5"><Label className="text-sm font-medium">Standardansvarlig rolle</Label>
                <Select value={draft.default_responsible_role} onValueChange={(v) => setDraft({ ...draft, default_responsible_role: v })}>
                  <SelectTrigger data-testid="new-step-role" className="rounded-xl h-10 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>{roleOpts.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 sm:col-span-2"><Label className="text-sm font-medium">Beskrivelse / instruktion</Label><Textarea rows={2} data-testid="new-step-desc" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="rounded-xl bg-white" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={draft.default_selected} onCheckedChange={(c) => setDraft({ ...draft, default_selected: !!c })} data-testid="new-step-selected" className="border-[#D6D3D1] data-[state=checked]:bg-[#A83232] data-[state=checked]:border-[#A83232]" /> Tilvalgt som standard</label>
            <div className="flex gap-2">
              <Button size="sm" className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]" onClick={addStep} data-testid="save-new-step"><Check className="h-4 w-4 mr-1" /> Tilføj trin</Button>
              <Button size="sm" variant="ghost" className="rounded-xl" onClick={() => setAdding(false)}>Annullér</Button>
            </div>
          </div>
        ) : (
          <button type="button" onClick={() => setAdding(true)} data-testid="add-step-btn" className="w-full flex items-center justify-center gap-2 rounded-xl border border-dashed border-[#D6D3D1] text-stone-500 hover:text-[#A83232] hover:border-[#A83232] hover:bg-[#F1DEDA]/40 [transition-property:color,border-color,background-color] duration-200 py-2.5 text-sm font-medium">
            <Plus className="h-4 w-4" /> Tilføj trin
          </button>
        )}

        <DialogFooter><Button variant="outline" className="rounded-xl border-[#EBE8E3]" onClick={onClose}>Luk</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StepRow({ step, index, count, onMove, onChanged }) {
  const [s, setS] = useState(step);
  useEffect(() => setS(step), [step]);

  const persist = async (next) => {
    try {
      await api.put(`/template-steps/${step.id}`, {
        name: next.name, description: next.description || "",
        default_duration_days: parseInt(next.default_duration_days) || 0,
        default_responsible_role: next.default_responsible_role,
        default_selected: next.default_selected, active: next.active,
      });
      onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setField = (patch) => { const next = { ...s, ...patch }; setS(next); persist(next); };
  const blurSave = (field) => { if (String(s[field] ?? "") !== String(step[field] ?? "")) persist(s); };
  const del = async () => { await api.delete(`/template-steps/${step.id}`); toast.success("Trin slettet"); onChanged(); };

  return (
    <div className={`border border-[#EBE8E3] rounded-2xl p-3.5 bg-white ${s.active ? "" : "opacity-60"}`} data-testid={`step-row-${step.id}`}>
      <div className="flex items-start gap-2">
        <div className="flex flex-col">
          <Button size="icon" variant="ghost" className="h-6 w-6 rounded-lg" disabled={index === 0} onClick={() => onMove(index, -1)} data-testid={`step-up-${step.id}`}><ChevronUp className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="h-6 w-6 rounded-lg" disabled={index === count - 1} onClick={() => onMove(index, 1)} data-testid={`step-down-${step.id}`}><ChevronDown className="h-4 w-4" /></Button>
        </div>
        <span className="h-6 w-6 mt-1 rounded-full bg-[#A83232]/10 text-[#A83232] text-[11px] font-semibold flex items-center justify-center shrink-0">{index + 1}</span>
        <div className="flex-1 grid sm:grid-cols-6 gap-2 items-center">
          <Input className="sm:col-span-3 rounded-xl" value={s.name} onChange={(e) => setS({ ...s, name: e.target.value })} onBlur={() => blurSave("name")} data-testid={`step-name-${step.id}`} />
          <Input type="number" min="0" className="sm:col-span-1 rounded-xl" value={s.default_duration_days} onChange={(e) => setS({ ...s, default_duration_days: e.target.value })} onBlur={() => blurSave("default_duration_days")} data-testid={`step-duration-${step.id}`} />
          <Select value={s.default_responsible_role || "medarbejder"} onValueChange={(v) => setField({ default_responsible_role: v })}>
            <SelectTrigger className="sm:col-span-2 rounded-xl" data-testid={`step-role-${step.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{roleOpts.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-2 pl-9">
        <label className="flex items-center gap-2 text-xs"><Checkbox checked={s.default_selected} onCheckedChange={(c) => setField({ default_selected: !!c })} data-testid={`step-selected-${step.id}`} className="border-[#D6D3D1] data-[state=checked]:bg-[#A83232] data-[state=checked]:border-[#A83232]" /> Tilvalgt som standard</label>
        <label className="flex items-center gap-2 text-xs"><Switch checked={s.active} onCheckedChange={(c) => setField({ active: !!c })} data-testid={`step-active-${step.id}`} /> Aktiv</label>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="ghost" className="rounded-xl h-8 text-stone-400 hover:text-destructive hover:bg-[#F1DEDA]" onClick={del} data-testid={`delete-step-${step.id}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  );
}
